import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import {
  COLLECTION_FAMILIES,
  EMPTY_DAILY_MISSIONS,
  MISSION_PACK_THRESHOLD,
  RANKED_DAILY_COUNT,
  collectionFactsFromSources,
  collectionPendingCount,
  collectionProgress,
  collectionTrackViews,
  isCollectionMilestoneId,
  isPackId,
  MISSION_COIN_REWARD,
  normalizeRankedDaily,
  rankedPendingCount,
  rankedStepCoins,
  runnerRows,
  type CollectionClaimResult,
  type CollectionFamily,
  type DailyMissionsSnapshot,
  type MissionId,
  type PackId,
  type RunnerMissionId,
  type RunnerMissionRow,
} from '@shared/index'
import { api, ApiError } from '../api/http'
import { allCatalogCards } from '../game/packs/packCatalog'
import { useAuthStore } from './auth'
import { useCollectionStore } from './collection'
import { useWalletStore } from './wallet'

const COLLECTION_FACTS = collectionFactsFromSources(
  allCatalogCards().map((card) => ({
    id: card.id,
    type: card.type,
    attribute: card.attribute,
    race: card.race,
    card_sets: card.card_sets,
  })),
)

function copySnap(snap: DailyMissionsSnapshot): DailyMissionsSnapshot {
  const rows = Array.isArray(snap.rows) ? snap.rows : []
  const collected = Array.isArray(snap.collected) ? snap.collected : []
  const railClaimed = Number.isFinite(snap.railClaimed)
    ? Math.max(0, Math.trunc(snap.railClaimed))
    : 0
  const ranked = normalizeRankedDaily(
    snap.rankedPlayed,
    snap.rankedCollected,
    snap.rankedPackGranted,
    snap.rankedStep7PackGranted,
  )
  const newlyRanked = typeof snap.newlyRanked === 'number' &&
    Number.isInteger(snap.newlyRanked) &&
    snap.newlyRanked >= 1 &&
    snap.newlyRanked <= RANKED_DAILY_COUNT
    ? snap.newlyRanked
    : undefined
  return {
    day: snap.day,
    rows: rows.map((row) => ({ ...row })),
    completed: snap.completed,
    packGranted: snap.packGranted,
    collected: [...collected],
    railClaimed,
    rankedPlayed: ranked.played,
    rankedCollected: ranked.collected,
    rankedPackGranted: ranked.packGranted,
    rankedStep7PackGranted: ranked.midPackGranted,
    runner: Array.isArray(snap.runner) ? snap.runner.map((row) => ({ ...row })) : runnerRows(0, 0, []),
    newlyCompleted: snap.newlyCompleted ? [...snap.newlyCompleted] : undefined,
    newlyRanked,
    coins: snap.coins,
    packs: snap.packs ? { ...snap.packs } : undefined,
  }
}

/**
 * Today's daily missions. Hydrated from /account/state and /missions.
 */
export const useMissionsStore = defineStore('missions', () => {
  const day = ref('')
  const rows = ref(EMPTY_DAILY_MISSIONS.rows)
  const completed = ref(0)
  const packGranted = ref(false)
  const collected = ref<MissionId[]>([])
  const railClaimed = ref(0)
  const rankedPlayed = ref(0)
  const rankedCollected = ref(0)
  const rankedPackGranted = ref(false)
  const rankedStep7PackGranted = ref(false)
  const runnerRowsState = ref<RunnerMissionRow[]>(EMPTY_DAILY_MISSIONS.runner)
  const lastSnapshot = ref<DailyMissionsSnapshot | null>(null)
  const busy = ref(false)
  const loadError = ref(false)
  const claimToastIds = ref<MissionId[]>([])
  const rankedToastStep = ref<number | null>(null)
  const collectionClaimed = ref<string[]>([])

  const collectionRows = computed(() =>
    collectionProgress(
      useCollectionStore().copies,
      COLLECTION_FACTS,
      collectionClaimed.value,
      useCollectionStore().album,
    ),
  )
  const collectionViews = computed(() => collectionTrackViews(collectionRows.value))
  const collectionPending = computed(() => collectionPendingCount(collectionRows.value))
  const runnerPending = computed(
    () => runnerRowsState.value.filter((row) => row.done && !row.claimed).length,
  )

  const pendingIds = computed(() =>
    rows.value
      .filter((row) => row.done && !collected.value.includes(row.id))
      .map((row) => row.id),
  )
  const pendingCount = computed(() => pendingIds.value.length)
  const railPending = computed(() =>
    Math.max(
      0,
      Math.min(completed.value, MISSION_PACK_THRESHOLD) - railClaimed.value,
    ),
  )
  const rankedPending = computed(() =>
    rankedPendingCount(rankedPlayed.value, rankedCollected.value),
  )
  const claimableCount = computed(
    () =>
      pendingCount.value +
      railPending.value +
      rankedPending.value +
      collectionPending.value +
      runnerPending.value,
  )
  /**
   * Coins already in the wallet that the chip hides until Collect.
   * Daily missions and race goals pay 3. Ranked steps 1–9 pay 3; step 10 pays packs.
   * The rail pays on the click, so it is not held here.
   */
  const heldCoins = computed(() => {
    const board =
      (pendingCount.value + runnerPending.value) * MISSION_COIN_REWARD
    let ranked = 0
    const played = rankedPlayed.value
    const collected = rankedCollected.value
    for (let step = collected + 1; step <= played; step += 1) {
      ranked += rankedStepCoins(step)
    }
    return board + ranked
  })

  function rememberClaimed(ids: readonly string[]): void {
    if (ids.length === 0) return
    const next = new Set(collectionClaimed.value)
    for (const id of ids) {
      if (isCollectionMilestoneId(id)) next.add(id)
    }
    collectionClaimed.value = [...next]
  }

  function applyPackInventory(raw: Record<string, number> | undefined): void {
    if (!raw) return
    const collection = useCollectionStore()
    const next = { ...collection.packs }
    let changed = false
    for (const id of Object.keys(next) as PackId[]) {
      const qty = raw[id]
      if (!isPackId(id) || !Number.isInteger(qty) || qty < 0) continue
      next[id] = qty
      changed = true
    }
    if (changed) collection.applyPacks(next)
  }

  function applyCollectionClaim(result: CollectionClaimResult): void {
    if (Array.isArray(result.known)) setCollectionClaimed(result.known)
    else rememberClaimed(result.claimed)
    if (typeof result.coins === 'number') useWalletStore().setCoins(result.coins)
    applyPackInventory(result.packs)
  }

  function applyRunner(rows: RunnerMissionRow[]): void {
    if (!Array.isArray(rows) || rows.length === 0) return
    runnerRowsState.value = rows.map((row) => ({ ...row }))
  }

  function apply(snap: DailyMissionsSnapshot): void {
    const next = copySnap(snap)
    lastSnapshot.value = next
    day.value = next.day
    rows.value = next.rows
    completed.value = next.completed
    packGranted.value = next.packGranted
    collected.value = next.collected
    railClaimed.value = next.railClaimed
    rankedPlayed.value = next.rankedPlayed
    rankedCollected.value = next.rankedCollected
    rankedPackGranted.value = next.rankedPackGranted
    rankedStep7PackGranted.value = next.rankedStep7PackGranted
    runnerRowsState.value = next.runner
    const fresh = next.newlyCompleted ?? []
    if (fresh.length > 0) {
      const seen = new Set(claimToastIds.value)
      for (const id of fresh) {
        if (!seen.has(id)) {
          seen.add(id)
          claimToastIds.value = [...claimToastIds.value, id]
        }
      }
    }
    if (next.newlyRanked) rankedToastStep.value = next.newlyRanked
    if (next.packs) {
      useCollectionStore().applyPacks(next.packs as Record<PackId, number>)
    }
  }

  function dismissClaimToast(): void {
    claimToastIds.value = []
    rankedToastStep.value = null
  }

  function clear(): void {
    day.value = ''
    rows.value = []
    completed.value = 0
    packGranted.value = false
    collected.value = []
    railClaimed.value = 0
    rankedPlayed.value = 0
    rankedCollected.value = 0
    rankedPackGranted.value = false
    rankedStep7PackGranted.value = false
    runnerRowsState.value = runnerRows(0, 0, [])
    lastSnapshot.value = null
    loadError.value = false
    claimToastIds.value = []
    rankedToastStep.value = null
    collectionClaimed.value = []
  }

  function setCollectionClaimed(ids: readonly string[]): void {
    collectionClaimed.value = ids.filter(isCollectionMilestoneId)
  }

  async function refresh(): Promise<void> {
    const auth = useAuthStore()
    if (!auth.token) {
      clear()
      loadError.value = false
      return
    }
    try {
      const payload = await api<DailyMissionsSnapshot>('/missions', {
        token: auth.token,
      })
      apply(payload)
      loadError.value = false
      return
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        clear()
        loadError.value = false
        return
      }
      loadError.value = true
    }
  }

  async function collect(missionId: MissionId): Promise<DailyMissionsSnapshot | null> {
    const auth = useAuthStore()
    if (!auth.token || busy.value) return null
    if (!pendingIds.value.includes(missionId)) return null
    busy.value = true
    try {
      const payload = await api<DailyMissionsSnapshot>('/missions/collect', {
        method: 'POST',
        token: auth.token,
        body: JSON.stringify({ missionId }),
      })
      apply(payload)
      return payload
    } catch {
      return null
    } finally {
      busy.value = false
    }
  }

  async function collectAll(): Promise<DailyMissionsSnapshot | null> {
    const auth = useAuthStore()
    if (!auth.token || busy.value) return null
    if (pendingIds.value.length === 0 && railPending.value <= 0) return null
    busy.value = true
    try {
      const payload = await api<DailyMissionsSnapshot>('/missions/collect-all', {
        method: 'POST',
        token: auth.token,
      })
      apply(payload)
      if (typeof payload.coins === 'number') {
        useWalletStore().setCoins(payload.coins)
      }
      return payload
    } catch {
      return null
    } finally {
      busy.value = false
    }
  }

  async function collectRanked(step: number): Promise<DailyMissionsSnapshot | null> {
    const auth = useAuthStore()
    if (!auth.token || busy.value) return null
    if (step > rankedPlayed.value || step <= rankedCollected.value) return null
    busy.value = true
    try {
      const payload = await api<DailyMissionsSnapshot>('/missions/collect-ranked', {
        method: 'POST',
        token: auth.token,
        body: JSON.stringify({ step }),
      })
      apply(payload)
      return payload
    } catch {
      return null
    } finally {
      busy.value = false
    }
  }

  async function collectCollection(
    id: string,
  ): Promise<CollectionClaimResult | null> {
    const auth = useAuthStore()
    if (!auth.token || busy.value) return null
    const row = collectionRows.value.find((item) => item.id === id)
    if (!row?.done || row.claimed) return null
    busy.value = true
    try {
      const payload = await api<CollectionClaimResult>('/missions/collection/claim', {
        method: 'POST',
        token: auth.token,
        body: JSON.stringify({ id }),
      })
      applyCollectionClaim(payload)
      return payload
    } catch (err) {
      if (err instanceof ApiError && err.code === 'already_claimed') {
        rememberClaimed([id])
        return { claimed: [], coins: useWalletStore().coins }
      }
      return null
    } finally {
      busy.value = false
    }
  }

  async function collectAllCollection(): Promise<CollectionClaimResult | null> {
    const auth = useAuthStore()
    if (!auth.token || busy.value || collectionPending.value < 1) return null
    busy.value = true
    try {
      const payload = await api<CollectionClaimResult>(
        '/missions/collection/claim-all',
        { method: 'POST', token: auth.token },
      )
      applyCollectionClaim(payload)
      return payload
    } catch {
      return null
    } finally {
      busy.value = false
    }
  }

  async function collectRunner(
    id: RunnerMissionId,
  ): Promise<DailyMissionsSnapshot | null> {
    const auth = useAuthStore()
    if (!auth.token || busy.value) return null
    const row = runnerRowsState.value.find((item) => item.id === id)
    if (!row?.done || row.claimed) return null
    busy.value = true
    try {
      const payload = await api<DailyMissionsSnapshot>('/missions/runner/claim', {
        method: 'POST',
        token: auth.token,
        body: JSON.stringify({ id }),
      })
      apply(payload)
      if (typeof payload.coins === 'number') {
        useWalletStore().setCoins(payload.coins)
      }
      return payload
    } catch {
      return null
    } finally {
      busy.value = false
    }
  }

  async function claimStep(): Promise<DailyMissionsSnapshot | null> {
    const auth = useAuthStore()
    if (!auth.token || busy.value) return null
    if (railPending.value <= 0) return null
    busy.value = true
    try {
      const payload = await api<DailyMissionsSnapshot>('/missions/claim-step', {
        method: 'POST',
        token: auth.token,
      })
      apply(payload)
      if (typeof payload.coins === 'number') {
        useWalletStore().setCoins(payload.coins)
      }
      return payload
    } catch {
      return null
    } finally {
      busy.value = false
    }
  }

  return {
    day,
    rows,
    completed,
    packGranted,
    collected,
    railClaimed,
    rankedPlayed,
    rankedCollected,
    rankedPackGranted,
    rankedStep7PackGranted,
    lastSnapshot,
    pendingIds,
    pendingCount,
    heldCoins,
    railPending,
    rankedPending,
    claimableCount,
    busy,
    loadError,
    claimToastIds,
    rankedToastStep,
    collectionClaimed,
    collectionRows,
    collectionViews,
    collectionPending,
    runnerRows: runnerRowsState,
    runnerPending,
    collectionFamilies: COLLECTION_FAMILIES as readonly CollectionFamily[],
    apply,
    applyRunner,
    dismissClaimToast,
    clear,
    setCollectionClaimed,
    refresh,
    collect,
    collectAll,
    collectRanked,
    claimStep,
    collectCollection,
    collectAllCollection,
    collectRunner,
  }
})
